import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { platformApi } from '@/services/platformApi';
import { LoadingSpinner } from '@vaiu/ui';
import { ErrorMessage } from '@vaiu/ui';
import { i18n } from '@/utils/i18n';
import PublicProfileCard from '@/components/profile/PublicProfileCard';
import MerchantProfilePreview from '@/components/merchant/onboarding/MerchantProfilePreview';
import type { PublicUserProfile } from '@/services/api/UserApiClient';
import { transformMerchantProfileForPreview, transformMerchantSocialMedia } from '@/utils/merchantProfileTransformers';

interface UserPublicProfileProps {
  previewMode?: boolean;
  previewData?: PublicUserProfile;
}

const UserPublicProfile: React.FC<UserPublicProfileProps> = ({ 
  previewMode = false, 
  previewData 
}) => {
  const { userId } = useParams<{ userId: string }>();
  const [userProfile, setUserProfile] = useState<PublicUserProfile | null>(previewData || null);
  const [loading, setLoading] = useState(!previewMode);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      // Skip loading if in preview mode
      if (previewMode && previewData) {
        setUserProfile(previewData);
        setLoading(false);
        return;
      }
      
      if (!userId) {
        setError('User ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await platformApi.user.getPublicUserProfile(userId);
        
        console.log('Loaded user public profile:', response);
        setUserProfile(response);
        
      } catch (err: any) {
        console.error('Failed to load user profile:', err);
        setError(err.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [userId, previewMode, previewData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" message={i18n.__('userPublicProfile.loading') || 'Loading profile...'} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorMessage 
          title={i18n.__('userPublicProfile.notFound') || 'Profile Not Found'}
          message={error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6">
            <Alert>
              <AlertDescription>
                {i18n.__('userPublicProfile.notFoundDesc') || 'User profile not found'}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user has MERCHANT role profile
  const merchantRoleProfile = userProfile.public?.otherProfiles?.find(
    (p: any) => p.role === 'MERCHANT'
  );

  // Extract merchant profile data if available
  const merchantProfile = merchantRoleProfile?.profile;

  // Prepare profile data for PublicProfileCard
  const profileData = {
    id: userProfile.id,
    name: userProfile.username || `User ${userProfile.id.slice(-6)}`,
    displayName: userProfile.username || `User ${userProfile.id.slice(-6)}`,
    username: userProfile.username,
    email: undefined, // May not be in public profile
    phone: undefined, // May not be in public profile
    avatar: undefined, // May not be in public profile
    isMerchant: !!merchantProfile,
    verification: {
      isVerified: merchantProfile?.status?.verified || merchantProfile?.verification?.isVerified || false,
      kycStatus: merchantProfile?.status?.kycStatus || merchantProfile?.verification?.kycStatus || undefined,
      kybStatus: merchantProfile?.status?.kybStatus || merchantProfile?.verification?.kycStatus || undefined
    },
    isPublished: merchantProfile?.settings?.visibility?.isPublished ?? merchantProfile?.status?.isPublished ?? false
  };

  // Prepare merchant profile data for MerchantProfilePreview using transformer utility
  const merchantPreviewData = merchantProfile 
    ? transformMerchantProfileForPreview(merchantProfile, userProfile.username || `User ${userProfile.id.slice(-6)}`)
    : undefined;

  const merchantSocialMedia = transformMerchantSocialMedia(merchantProfile);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* User Profile Header */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                <User className="w-8 h-8" />
              </div>
              <div>
                <CardTitle className="text-2xl">{userProfile.username || `User ${userProfile.id.slice(-6)}`}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {i18n.__('userPublicProfile.memberSince') || 'Member since'} {
                    (() => {
                      try {
                        return new Date(userProfile.createdAt).toLocaleDateString();
                      } catch {
                        return i18n.__('common.unknown') || 'Unknown';
                      }
                    })()
                  }
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Merchant Profile Preview - Show if user has MERCHANT role */}
        {merchantProfile && merchantPreviewData ? (
          <MerchantProfilePreview
            businessInfo={merchantPreviewData}
            socialMedia={merchantSocialMedia}
            isMinimized={false}
          />
        ) : (
          /* User Profile Card - Compact view for regular users */
          <PublicProfileCard 
            profile={profileData}
          />
        )}

        {/* Additional User Information */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{i18n.__('userPublicProfile.profileInformation') || 'Profile Information'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-500">{i18n.__('userPublicProfile.userId') || 'User ID'}</p>
                <p className="text-gray-900 font-mono text-sm" aria-label={`User ID: ${userProfile.id}`}>{userProfile.id}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">{i18n.__('userPublicProfile.username') || 'Username'}</p>
                <p className="text-gray-900">{userProfile.username || (i18n.__('userPublicProfile.notSet') || 'Not set')}</p>
              </div>
              {userProfile.public?.otherProfiles && userProfile.public.otherProfiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">{i18n.__('userPublicProfile.roles') || 'Roles'}</p>
                  <div className="flex flex-wrap gap-2" role="list" aria-label="User roles">
                    {userProfile.public.otherProfiles.map((roleProfile: any, index: number) => (
                      <Badge key={index} variant="secondary" role="listitem">
                        {roleProfile.role}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserPublicProfile;

